import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Calendar, Video } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CONSULT } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Consult = () => {
  const { user } = useApp();
  const [astrologers, setAstrologers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: "", time: "", name: user?.name || "", question: "" });

  useEffect(() => {
    api.get("/astrologers").then((r) => setAstrologers(r.data)).catch(() => {});
  }, []);

  const openBooking = (id) => {
    setOpenId(id);
    setForm({ date: "", time: "", name: user?.name || "", question: "" });
  };

  const book = async (astrologer) => {
    if (!form.date || !form.time || !form.name) { toast.error("Please pick date, time and enter your name"); return; }
    const slot = `${form.date}T${form.time}`;
    setLoading(true);
    try {
      const r = await api.post("/consultations/checkout", {
        astrologer_id: astrologer.id,
        slot,
        name: form.name,
        question: form.question,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
      setLoading(false);
    }
  };

  const minDate = new Date().toISOString().split("T")[0];

  return (
    <div data-testid={CONSULT.page} className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
      <p className="text-xs uppercase tracking-label text-secondary">Ask an Astrologer</p>
      <h1 className="display-serif text-5xl md:text-6xl mt-3">A private consultation, one-on-one.</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl">
        Book a 30-minute private video consultation with a verified Vedic astrologer. Ask anything about your Kundli, career, marriage or spiritual practice — you receive a personal Google Meet link after payment.
      </p>

      <div className="mt-14 space-y-8">
        {astrologers.map((a) => (
          <div key={a.id} data-testid={CONSULT.astrologerCard(a.id)} className="editorial-card p-6 md:p-8">
            <div className="grid md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-3">
                <img src={a.avatar} alt={a.name} className="w-full aspect-square object-cover border border-border" />
              </div>
              <div className="md:col-span-6">
                <p className="text-[10px] uppercase tracking-label text-secondary">{a.specialty}</p>
                <h2 className="font-serif text-2xl md:text-3xl mt-2">{a.name}</h2>
                <p className="text-sm text-muted-foreground mt-3">{a.bio}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {a.languages.map((l) => (
                    <span key={l} className="text-[10px] uppercase tracking-label px-2 py-1 border border-border">{l}</span>
                  ))}
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-label text-muted-foreground">
                  {a.experience_years} yrs · {a.duration_min} min session
                </div>
              </div>
              <div className="md:col-span-3 text-right">
                <p className="font-serif text-3xl">${a.price_usd.toFixed(0)}</p>
                <p className="text-[10px] uppercase tracking-label text-muted-foreground mt-1">per session</p>
                <button
                  data-testid={CONSULT.bookBtn(a.id)}
                  onClick={() => openBooking(a.id)}
                  className="mt-5 w-full px-4 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn hover:bg-primary/90"
                >
                  Book slot
                </button>
              </div>
            </div>

            {openId === a.id && (
              <div className="mt-8 pt-8 border-t border-border grid md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-label">Date</Label>
                  <Input type="date" min={minDate} data-testid={CONSULT.slotDate} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-label">Time</Label>
                  <Input type="time" data-testid={CONSULT.slotTime} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-label">Your name</Label>
                  <Input data-testid={CONSULT.nameInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-label">Question (optional)</Label>
                  <Input data-testid={CONSULT.questionInput} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="e.g. marriage timing" className="mt-2 rounded-none border-0 border-b bg-transparent" />
                </div>
                <div className="md:col-span-4 flex justify-end">
                  <button
                    onClick={() => book(a)}
                    disabled={loading}
                    data-testid={CONSULT.confirmBtn}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" strokeWidth={1.5} />}
                    Pay ${a.price_usd.toFixed(0)} & confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!user && (
        <p className="mt-10 text-sm text-muted-foreground">
          Tip: <Link to="/login" className="text-primary underline-offset-4 hover:underline">Sign in</Link> so your booking appears in your <em>My Consultations</em> tab.
        </p>
      )}
    </div>
  );
};

export default Consult;
